<template>
  <v-row no-gutters>
    <v-col v-if="isFetched">
      <v-row no-gutters class="pb-4" align="center">
        <v-col cols="12">
          <v-row no-gutters align="baseline">
            <v-col cols="auto" class="pr-4 font-weight-bold">
              {{ $i18n.t('admin.dynamicDictionaries.title') }}
            </v-col>
            <v-col cols="auto">
              <base-rounded-button
                icon="mdi-plus"
                size="x-small"
                color="success"
                :tooltip="$i18n.t('admin.dynamicDictionaries.addDictionary')"
                @click="openAddEditDictDialog(false)"
              />
            </v-col>
            <v-spacer />
            <data-input v-model="searchValue" v-bind="searchInput" @input="search" />
          </v-row>
        </v-col>
      </v-row>
      <v-row no-gutters>
        <v-col cols="12" style="border: 1px solid rgba(0, 0, 0, 0.12);">
          <v-data-table
            :items="searchedRelationsDictionaries"
            :headers="headers"
            :footer-props="{
              itemsPerPageOptions: [20, 50, 100],
            }"
            :items-per-page="20"
            sort-by="id"
            :sort-desc="true"
          >
            <template #[`item.relatedDatasources`]="{ item }">
              <span style="display: flex; flex-direction: column; gap: 0.3rem">
                <v-row no-gutters v-for="(datasource, index) in [...item.used_in_ds].splice(0, 3)" :key="index">
                  <v-col class="caption" style="text-align: center; line-height: 0.9rem">
                    {{ datasource }}
                  </v-col>
                </v-row>
              </span>
              <v-row no-gutters v-if="[...item.used_in_ds].length > 3">
                <v-col>
                  <v-tooltip bottom color="font">
                    <template #activator="{ on }">
                      <v-chip x-small v-on="on">...</v-chip>
                    </template>
                    <div v-for="(remainingDatasource, index) in [...item.used_in_ds].slice(3)" :key="index">
                      {{ remainingDatasource }}
                    </div>
                  </v-tooltip>
                </v-col>
              </v-row>
            </template>
            <template #[`item.actions`]="{ item }">
              <dots-menu
                :items="dotsMenuItems"
                @edit="openAddEditDictDialog(true, item)"
                @delete="openDeleteDialog(item)"
              />
            </template>
          </v-data-table>
        </v-col>
      </v-row>
    </v-col>
    <v-col v-else cols="12">
      <main-loader />
    </v-col>
    <main-dialog
      :is-visible.sync="isDeleteDictVisible"
      v-if="isDeleteDictLoaded"
      :title="$i18n.t('dialog.deleteDictionary')"
      :body="$i18n.t('dialog.confirmDictionaryDelete')"
      agree-text="dialog.agreeDelete"
      :loading="isDeleteDictLoading"
      @agree="handleDeleteDict"
    ></main-dialog>
    <main-input-dialog
      dialog-width="40%"
      v-if="isAddEditDictLoaded"
      :is-visible.sync="isAddEditDictVisible"
      :loading="isAddEditDictLoading"
      :title-translation="$i18n.t(`admin.dynamicDictionaries.${isDictEditing ? 'editDict' : 'addDict'}`)"
      :agree-text="`dialog.${isDictEditing ? 'agreeEdit' : 'agreeAdd'}`"
      @agree="handleDict"
      :inputs="dictInputs"
    >
      <template v-for="input of dictInputs" #[`${input.name}.input`]="data">
        <v-col :key="input.name" cols="6">
          <data-input
            v-model="addEditDictPayload[input.name]"
            v-bind="data"
            @change="onInputValueChange(input.name, $event)"
          />
        </v-col>
      </template>
    </main-input-dialog>
  </v-row>
</template>

<script>
import DotsMenu from '@/components/DotsMenu';
import MainLoader from '@/components/MainLoader';
import DataInput from '@/components/DataInput';

import { get, call } from 'vuex-pathify';
import { debounce, get as lodashGet } from 'lodash';

export default {
  name: 'DynamicDictionariesSettingsList',
  components: {
    DotsMenu,
    MainLoader,
    DataInput,
    MainDialog: () => import('@/components/MainDialog'),
    MainInputDialog: () => import('@/components/MainInputDialog'),
  },
  data: () => ({
    isDeleteDictVisible: false,
    isDeleteDictLoaded: false,
    isDeleteDictLoading: false,
    isAddEditDictLoaded: false,
    isAddEditDictLoading: false,
    isAddEditDictVisible: false,
    isDictEditing: false,
    currentDictId: null,
    currentDataSourceAttributes: null,
    isFetched: false,
    addEditDictPayload: {},
    dotsMenuItems: [
      {
        name: 'edit',
      },
      {
        name: 'delete',
      },
    ],
    searchValue: '',
    searchedRelationsDictionaries: [],
  }),
  computed: {
    dataSources: get('layers/metadata'),
    relationsDictionaries: get('admin/relationsDictionaries'),
    relationsDictionariesMapped() {
      return (
        this.relationsDictionaries?.map(dict => {
          return {
            ...dict,
            verboses: {
              datasource: this.dataSources[dict.datasource].verbose_name,
              representation: this.getDatasourceAttributeVerboseName(dict.datasource, dict.representation),
              attribute: this.getDatasourceAttributeVerboseName(dict.datasource, dict.attribute),
            },
          };
        }) || []
      );
    },
    dataSourcesArray() {
      return this.$_objectToArray(this.dataSources);
    },
    dictInputs() {
      return [
        {
          name: 'name',
          translationPath: 'admin.dynamicDictionaries.name',
          dataType: { name: 'text' },
          cols: '12',
          rules: [v => !!v || v == '0' || 'rules.required'],
        },
        {
          name: 'datasource',
          translationPath: 'admin.dynamicDictionaries.datasource',
          dataType: { name: 'select' },
          items: this.dataSourcesArray,
          itemValue: 'name',
          itemText: 'verbose_name',
          cols: '12',
          rules: [v => !!v || v == '0' || 'rules.required'],
        },
        {
          name: 'representation',
          translationPath: 'admin.dynamicDictionaries.verboseAttribute',
          subtitle: 'admin.dynamicDictionaries.verboseAttributeSubtitle',
          dataType: { name: 'select' },
          items: this.currentDataSourceAttributes || [],
          itemText: 'verbose_name',
          itemValue: 'name',
          disabled: !this.currentDataSourceAttributes,
          cols: '12',
          rules: [v => !!v || v == '0' || 'rules.required'],
        },
        {
          name: 'attribute',
          translationPath: 'admin.dynamicDictionaries.systemAttribute',
          subtitle: 'admin.dynamicDictionaries.systemAttributeSubtitle',
          dataType: { name: 'select' },
          items: this.currentDataSourceAttributes || [],
          itemText: 'verbose_name',
          itemValue: 'name',
          disabled: !this.currentDataSourceAttributes,
          cols: '12',
          rules: [v => !!v || v == '0' || 'rules.required'],
        },
      ];
    },
    headers() {
      return [
        {
          text: 'ID',
          value: 'id',
          align: 'center',
        },
        {
          text: this.$i18n.t('admin.dynamicDictionaries.name'),
          value: 'name',
          searchable: true,
          align: 'center',
        },
        {
          text: this.$i18n.t('admin.dynamicDictionaries.datasource'),
          value: 'verboses.datasource',
          searchable: true,
          align: 'center',
        },
        {
          text: this.$i18n.t('admin.dynamicDictionaries.verboseAttribute'),
          value: 'verboses.representation',
          searchable: true,
          align: 'center',
        },
        {
          text: this.$i18n.t('admin.dynamicDictionaries.systemAttribute'),
          value: 'verboses.attribute',
          searchable: true,
          align: 'center',
        },
        {
          text: this.$i18n.t('admin.dynamicDictionaries.relatedDatasources'),
          value: 'relatedDatasources',
          align: 'center',
          sortable: false,
        },
        { text: '', value: 'actions', sortable: false },
      ];
    },
    searchableHeadersValues() {
      return this.headers.filter(header => header.searchable).map(header => header.value);
    },
    searchInput() {
      return {
        dataType: { name: 'text' },
        placeholder: this.$i18n.t('default.find'),
        appendIcon: 'mdi-magnify',
        hideDetails: true,
      };
    },
  },
  methods: {
    getDataSources: call('layers/getLayersMetadata'),
    ...call('admin/*RelationsDict*'),
    openAddEditDictDialog(isEditing = false, item) {
      this.addEditDictPayload = {};
      this.currentDataSourceAttributes = this.currentDictId = null;
      this.isDictEditing = isEditing;
      this.isAddEditDictVisible = this.isAddEditDictLoaded = true;
      if (isEditing) {
        this.currentDictId = item.id;
        this.currentDataSourceAttributes = this.dataSources[item.datasource]?.attributes_schema.attributes;
        let itemCopy = JSON.parse(JSON.stringify(item));
        delete itemCopy.verboses;
        ['attribute', 'representation'].forEach(name => {
          itemCopy[name] = this.currentDataSourceAttributes.find(attr => attr.id === itemCopy[name])?.name;
        });
        this.addEditDictPayload = JSON.parse(JSON.stringify(itemCopy));
      }
    },
    onInputValueChange(inputName, value) {
      if (inputName === 'datasource') {
        this.addEditDictPayload.attribute = this.addEditDictPayload.representation = null;
        this.currentDataSourceAttributes = this.dataSources[value]?.attributes_schema.attributes;
      }
    },
    openDeleteDialog(item) {
      this.currentDictId = item.id;
      this.isDeleteDictVisible = this.isDeleteDictLoaded = true;
    },
    getDatasourceAttributeVerboseName(datasource, attribute) {
      const searchedDatasource = this.dataSources[datasource];
      const datasourceAttributes = searchedDatasource.attributes_schema.attributes;
      return datasourceAttributes.find(attr => attr.id === attribute)?.verbose_name;
    },
    search(value = this.searchValue) {
      if (!value) this.searchedRelationsDictionaries = [...this.relationsDictionariesMapped];
      else
        this.searchedRelationsDictionaries = [...this.relationsDictionariesMapped].filter(dict => {
          return this.searchableHeadersValues.some(header => {
            return lodashGet(dict, header)
              ?.toLocaleLowerCase()
              .includes(value?.toLocaleLowerCase());
          });
        });
    },
    async handleDeleteDict() {
      this.isDeleteDictLoading = true;
      try {
        await this.deleteRelationsDictionary(this.currentDictId);
        this.search();
      } finally {
        this.isDeleteDictLoading = this.isDeleteDictVisible = false;
      }
    },
    async handleDict() {
      this.isAddEditDictLoading = true;
      try {
        if (this.isDictEditing) {
          const copyPayload = JSON.parse(JSON.stringify(this.addEditDictPayload));
          delete copyPayload.id;
          await this.putRelationsDictionary({
            id: this.currentDictId,
            payload: copyPayload,
          });
        } else {
          await this.postRelationsDictionary(this.addEditDictPayload);
        }
        this.search();
      } finally {
        this.isAddEditDictLoading = this.isAddEditDictVisible = false;
      }
    },
    async init() {
      await Promise.all([this.getDataSources({ with_attributes_schema: true }), this.getRelationsDictionaries()]);
      this.search();
      this.search = debounce(this.search, 500);
      this.isFetched = true;
    },
  },
  mounted() {
    this.init();
  },
};
</script>
